import { useCallback, useMemo } from 'react';
import { ExcludeNil } from '@types';
import { AppFCC } from '@types';
import { LinkProps } from '@managers/links';
import { PaginationProps } from '@kit/pagination/types';
import { NavigationLinkStyled, PageLinkStyled, PaginationNavigationIconContainerStyled, PaginationNextIconStyled, PaginationPagesContainerStyled, PaginationPrevIconStyled, PaginationProcessedPageStyled, PaginationStyled } from './styled';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
export const Pagination: AppFCC<PaginationProps> = props => {
  const {
    className,
    page,
    maxVisiblePages = 7,
    totalPages,
    getPageDescriptor,
    pageOnClick
  } = props;
  const firstPage = 1;
  const lastPage = totalPages;
  const halfVisiblePages = Math.ceil((maxVisiblePages - 1) / 2);
  const leftBoundary = maxVisiblePages - halfVisiblePages;
  const rightBoundary = lastPage - maxVisiblePages + halfVisiblePages;
  let minVisiblePage = page < leftBoundary ? firstPage : page - halfVisiblePages;
  if (minVisiblePage + maxVisiblePages > lastPage) {
    minVisiblePage = lastPage - maxVisiblePages + 1;
  }
  const maxVisiblePage = page > rightBoundary ? lastPage : minVisiblePage + maxVisiblePages - 1;
  const prevPageDescriptor = useMemo(() => page > firstPage ? getPageDescriptor(page - 1) : undefined, [getPageDescriptor, page]);
  const nextPageDescriptor = useMemo(() => page < totalPages ? getPageDescriptor(page + 1) : undefined, [getPageDescriptor, page, totalPages]);
  const pageClick: ExcludeNil<LinkProps['onClick']> = useCallback(event => {
    const {
      page
    } = event.currentTarget.dataset;
    pageOnClick?.({
      page: Number(page),
      totalPages
    });
  }, [pageOnClick, totalPages]);
  const pagesArray = useMemo(() => Array.from(Array(totalPages)).reduce((processedPages, item, index) => {
    const processingPage = index + 1;
    const differenceBetweenNearestPages = processingPage - ([...processedPages].reverse()[0] || 0);
    if (differenceBetweenNearestPages > 1 && (processingPage === lastPage || processingPage === minVisiblePage)) {
      return [...processedPages, '...', processingPage];
    }
    if (differenceBetweenNearestPages > 1) {
      return [...processedPages, '...'];
    }
    if (processingPage !== 1 && processingPage !== lastPage && (processingPage < minVisiblePage || processingPage > maxVisiblePage)) {
      return processedPages;
    }
    return [...processedPages, processingPage];
  }, []), [lastPage, maxVisiblePage, minVisiblePage, totalPages]);
  const pages = useMemo(() => {
    return pagesArray.map((processingPage: string | number, index: number) => {
      switch (typeof processingPage) {
        case 'string':
          return _jsx("span", {
            children: processingPage
          }, index);
        case 'number':
        default:
          {
            const pageDescriptor = getPageDescriptor(processingPage);
            const isActivePage = processingPage === page;
            return isActivePage || !pageDescriptor ? _jsx(PaginationProcessedPageStyled, {
              children: processingPage
            }, index) : _jsx(PageLinkStyled //
            , {
              routeDescriptor: pageDescriptor,
              "data-page": pageDescriptor.params.page ?? firstPage,
              onClick: pageClick,
              children: processingPage
            }, index);
          }
      }
    });
  }, [getPageDescriptor, page, pageClick, pagesArray]);
  return _jsxs(PaginationStyled, {
    className: className,
    children: [prevPageDescriptor ? _jsx(NavigationLinkStyled //
    , {
      routeDescriptor: prevPageDescriptor,
      "data-page": prevPageDescriptor.params.page ?? firstPage,
      onClick: pageClick,
      children: _jsx(PaginationNavigationIconContainerStyled, {
        children: _jsx(PaginationPrevIconStyled, {})
      })
    }) : _jsx(PaginationNavigationIconContainerStyled, {
      disabled: true,
      children: _jsx(PaginationPrevIconStyled, {})
    }), _jsx(PaginationPagesContainerStyled, {
      children: pages
    }), nextPageDescriptor ? _jsx(NavigationLinkStyled //
    , {
      routeDescriptor: nextPageDescriptor,
      "data-page": nextPageDescriptor.params.page,
      onClick: pageClick,
      children: _jsx(PaginationNavigationIconContainerStyled, {
        children: _jsx(PaginationNextIconStyled, {})
      })
    }) : _jsx(PaginationNavigationIconContainerStyled, {
      disabled: true,
      children: _jsx(PaginationNextIconStyled, {})
    })]
  });
};